import { ANEX, AnexApiClient } from '@anex/common'
import { makeAutoObservable } from 'mobx'

import { RootStore } from './RootStore'
import { TokenManager } from '~/utils/TokenManager'
import { ITrustedFrame } from '~/types'

/**
 * API Calls or other data here should be related to the customer billing
 * ie: take payment, invoice, etc...
 */
export class BillingStore {
  api: AnexApiClient

  accountStatus: ANEX.Website.API.Endpoints.V1.Account.Status.StatusResponse | null =
    null

  transactions: ANEX.Website.API.Endpoints.V1.Account.Transactions.TransactionsResponse_Transaction[] =
    []

  invoices: ANEX.Website.API.Endpoints.V1.Account.Invoices.Summary.SummaryResponse_Invoice[] =
    []
  availablePaymentMethods: ANEX.Website.API.Endpoints.V1.Lookup.Payment.AvailablePaymentMethods.PaymentMethod[] =
    []

  trustedFrame: ITrustedFrame | null = null

  constructor(rootStore: RootStore) {
    this.api = rootStore.api

    makeAutoObservable(this)
  }

  get getAccountStatus() {
    return this.accountStatus
  }

  get getTransactions() {
    return this.transactions
  }

  get getInvoices() {
    return this.invoices
  }

  get getPaymentMethods() {
    return this.availablePaymentMethods
  }

  get getTrustedFrame() {
    return this.trustedFrame
  }

  *setAccountStatus(
    item: ANEX.Website.API.Endpoints.V1.Account.Status.StatusResponse | null
  ) {
    this.accountStatus = yield item || null
  }

  *setTransactions(
    items: ANEX.Website.API.Endpoints.V1.Account.Transactions.TransactionsResponse_Transaction[]
  ) {
    this.transactions = yield items
  }

  *setInvoices(
    items: ANEX.Website.API.Endpoints.V1.Account.Invoices.Summary.SummaryResponse_Invoice[]
  ) {
    this.invoices = yield items
  }

  *setAvailablePaymentMethods(
    items: ANEX.Website.API.Endpoints.V1.Lookup.Payment.AvailablePaymentMethods.PaymentMethod[]
  ) {
    this.availablePaymentMethods = yield items
  }

  *setTrustedFrame(tf: ITrustedFrame) {
    this.trustedFrame = yield tf
  }

  /**
   * Get Account Status
   */
  async getAccountStatusData() {
    return await this.api.V1.Account.Status(TokenManager.getTokenHeader())
      .then((response) => {
        this.setAccountStatus(response)
        return response
      })
      .catch((reason) => {
        // we can implement an error logging if needed
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Billing History/Transactions
   */
  async getBillingHistory(from: Date, to: Date) {
    return await this.api.V1.Account.Transactions(
      from,
      to,
      TokenManager.getTokenHeader()
    )
      .then((r) => {
        if (r && r.transactions) {
          const filteredTransactions = r.transactions.filter(
            (p: any) => p.paymentSuccessful
          )
          this.setTransactions(filteredTransactions)

          return filteredTransactions
        }
        return []
      })
      .catch((reason) => {
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Invoices
   */
  async getInvoicesData(from?: Date, to?: Date) {
    return await this.api.V1.Account.Invoices.Summary(
      from,
      to,
      TokenManager.getTokenHeader()
    )
      .then((r) => {
        if (r && r.invoices) {
          this.setInvoices(r.invoices)

          return r.invoices
        }
        return []
      })
      .catch((reason) => {
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Get Available Payment Methods
   */
  async getAvailablePaymentMethods() {
    return await this.api.V1.Lookup.Payment.AvailablePaymentMethods(
      TokenManager.getTokenHeader()
    )
      .then((r) =>
        r.availablePaymentMethods
          ? this.setAvailablePaymentMethods(r.availablePaymentMethods)
          : []
      )
      .catch((reason) => {
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }

  /**
   * Take Payment
   */
  async getTakePayment(
    payload: ANEX.Website.API.Endpoints.V1.CreditCard.TakePayment.TakePaymentRequest
  ) {
    return await this.api.V1.CreditCard.TakePayment(
      payload,
      TokenManager.getTokenHeader()
    )
      .then((resp) => {
        if (resp && resp.receiptCode) {
          return resp
        }
      })
      .catch((reason) => {
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        // this is a special case for the payment errors
        throw (
          reason.response?.data?.errors
            ?.map((err: { code: string; message: string }) => err.message)
            .join(' - ') ||
          'There was an error making a payment. Please try again.'
        )
      })
  }

  /**
   * Update Payment Details
   */
  async postUpdatePaymentDetails(
    payload: ANEX.Website.API.Endpoints.V1.Account.Actions.UpdatePaymentDetails.UpdatePaymentDetailsRequest
  ) {
    return await this.api.V1.Account.Actions.UpdatePaymentDetails(
      payload,
      TokenManager.getTokenHeader()
    )
      .then(() => true)
      .catch((reason) => {
        if (import.meta.env.MODE === 'development') {
          console.info(reason)
        }
        throw reason.message
      })
  }
}
